/**
 * How every setting works, in one registry, and the one way an editor shows it.
 *
 * WHY THE COPY LIVES HERE
 * -----------------------
 * Izi (1 Nov 2026): labels and helper text must say what each configuration
 * does, across the whole dashboard, "otherwise we might end up with
 * configurations that we don't fully understand how they work", and settings
 * must not conflict or duplicate each other. Copy scattered beside each input
 * cannot be read as a set, so two settings that quietly overlap never look
 * like they do. Here every explanation sits in one folder (help/), one file per
 * Settings section, and settingsHelp.test.js holds the set to the rules below.
 *
 * THE RULE FOR WRITING ONE
 * ------------------------
 * - The label names the thing in the reader's words ("Smallest basket we
 *   deliver"), not the storage key. The hint under the field says what a value
 *   does in one sentence and quotes the server's default.
 * - The tooltip body says: what it is, how the server uses it (when it is
 *   read, what it changes), what it does NOT do, and which other setting it
 *   works with or must not be confused with.
 * - Every fact is one the code enforces. A setting that is stored but read by
 *   nothing yet says so plainly.
 * - Plain sentences; no dashes as punctuation; no emoji.
 *
 * It mirrors components/fiscal/taxHelp.js and views/ThreePL/partnerHelp.js and
 * reuses components/HelpTooltip, the dashboard's one tooltip. Placement: the
 * tooltip sits inside the label or heading it explains, after the text.
 */
import React from "react";
import HelpTooltip from "../../components/HelpTooltip";
import { ORDERS_HELP } from "./help/ordersHelp";
import { DELIVERY_HELP } from "./help/deliveryHelp";
import { PAYMENTS_HELP } from "./help/paymentsHelp";
import { COMMISSION_TAX_HELP } from "./help/commissionTaxHelp";
import { TICKETING_HELP } from "./help/ticketingHelp";
import { NOTIFICATIONS_HELP } from "./help/notificationsHelp";
import { CATALOGUE_HELP } from "./help/catalogueHelp";
import { SUPPORT_HELP } from "./help/supportHelp";
import { SEED_CREDITS_HELP } from "./help/seedCreditsHelp";
import { ACCESS_HELP } from "./help/accessHelp";
import { VENDOR_SETTINGS_HELP } from "./help/vendorSettingsHelp";

/** Each section's entries, kept apart so the test can catch a topic defined twice. */
export const SETTINGS_HELP_BY_SECTION = Object.freeze({
  orders: ORDERS_HELP,
  delivery: DELIVERY_HELP,
  payments: PAYMENTS_HELP,
  commissionTax: COMMISSION_TAX_HELP,
  ticketing: TICKETING_HELP,
  notifications: NOTIFICATIONS_HELP,
  catalogue: CATALOGUE_HELP,
  support: SUPPORT_HELP,
  seedCredits: SEED_CREDITS_HELP,
  access: ACCESS_HELP,
  vendorSettings: VENDOR_SETTINGS_HELP,
});

export const SETTINGS_HELP = Object.freeze(
  Object.assign({}, ...Object.values(SETTINGS_HELP_BY_SECTION)),
);

/**
 * The tooltip beside a setting's label. Renders nothing for an unknown topic,
 * so a typo is invisible rather than an empty bubble; settingsHelp.test.js
 * checks every topic an editor names exists.
 */
const SettingHelp = ({ topic, placement = "top" }) => {
  const entry = SETTINGS_HELP[topic];
  if (!entry) return null;
  return (
    <HelpTooltip
      title={entry.title}
      body={entry.body}
      placement={placement}
      ariaLabel={`How ${entry.title} works`}
    />
  );
};

export default SettingHelp;
